// @flow
import { useQuery } from '@apollo/react-hooks';
import { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import Button from '@atlaskit/button';
import Drawer from '@atlaskit/drawer';
import Icon from '@atlaskit/icon';
import Page, { Grid, GridColumn } from '@atlaskit/page';
import PageHeader from '@atlaskit/page-header';
import { FlexContainer } from '@atlaskit/right-side-panel';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import PijenIcon from '../components/assets/pijenIcon';
import CreateWidgetModal from '../components/widgets/CreateWidgetModal';
import WidgetDetails from '../components/widgets/WidgetDetails';
import WidgetPermissionModal from '../components/widgets/WidgetPermissionModal';
import WidgetsTable from '../components/widgets/WidgetsTable';
import PermissionsContext from '../context/permissions';
import { GET_USER_PERMISSIONS } from '../graphql/queries';

const HeaderRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: flex-end;
    align-content: center;
    h1 {
        margin-bottom: 0;
    }
    .title {
        flex-grow: 1;
    }
    .button {
        flex-grow: 0;
        flex-basis: 148px;
    }
`;

const BreadcrumbsView = styled.div`
    * {
        cursor: default;

        &:hover {
            text-decoration: none !important;
            color: #6b778c !important;
        }
    }
    span.css-1h9rjj2 {
        margin-left: 7px;
    }
`;

const PageDescription = styled.div`
    font-size: 14px;
    padding-bottom: 20px;
    width: 45%;
`;

const PageWrapper = styled.div`
    padding: 0 24px;
    width: 100%;
`;

const breadcrumbs = (
    <BreadcrumbsView>
        <BreadcrumbsItem iconBefore={<Icon glyph={PijenIcon} label="Octo icon" size="medium" />} text="Pijen" />
    </BreadcrumbsView>
);

function Home() {
    const [isCreateWidgetModalOpen, setIsCreateWidgetModalOpen] = useState(false);
    const [displayWidgetPermissionModal, setDisplayWidgetPermissionModal] = useState();
    const [selectedWidget, setSelectedWidget] = useState(null);
    const [refetchData, setRefetchData] = useState(false);

    const getUserPermissionsQuery = useQuery(GET_USER_PERMISSIONS);

    useEffect(() => {
        if (refetchData) {
            setRefetchData(false);
        }
    }, [refetchData]);

    const permissions = _.get(getUserPermissionsQuery, 'data.getUserPermissions');
    const isAbleToCreateWidgets = (permissions || {}).hasAdministerPermission;
    const displayNoPermissionPanel =
        getUserPermissionsQuery.called &&
        !getUserPermissionsQuery.loading &&
        !permissions.hasAdministerPermission &&
        permissions.permissions.length === 0;

    return (
        <PermissionsContext.Provider value={permissions}>
            <Page>
                <Grid layout="fluid">
                    <GridColumn medium={12}>
                        <FlexContainer id="attach">
                            <PageWrapper>
                                <HeaderRow>
                                    <div className="title">
                                        <PageHeader breadcrumbs={breadcrumbs}>In-app notification widgets</PageHeader>
                                    </div>
                                    {isAbleToCreateWidgets && (
                                        <Button
                                            appearance="primary"
                                            onClick={() => {
                                                setIsCreateWidgetModalOpen(true);
                                            }}
                                        >
                                            Create new widget
                                        </Button>
                                    )}
                                    {isCreateWidgetModalOpen && (
                                        <CreateWidgetModal
                                            setIsOpen={setIsCreateWidgetModalOpen}
                                            onCreated={(widgetId) => {
                                                setRefetchData(true);
                                                setDisplayWidgetPermissionModal({ widgetId });
                                            }}
                                        />
                                    )}
                                </HeaderRow>
                                <PageDescription>
                                    Create notification widget, embed it in your application and send the update
                                    messages and collect feedback. Each widget has separate notification messages.
                                </PageDescription>
                                <WidgetsTable
                                    openCreateWidgetModal={setIsCreateWidgetModalOpen}
                                    openWidgetDetails={setSelectedWidget}
                                    refetchData={refetchData}
                                    displayNoPermissionPanel={displayNoPermissionPanel}
                                />
                                <Drawer
                                    isOpen={selectedWidget !== null}
                                    onClose={() => setSelectedWidget(null)}
                                    width="full"
                                >
                                    {selectedWidget && (
                                        <WidgetDetails
                                            widget={selectedWidget}
                                            closePage={() => setSelectedWidget(null)}
                                            onAddRemoveEditors={(widgetId) =>
                                                setDisplayWidgetPermissionModal({ widgetId, managing: true })
                                            }
                                        />
                                    )}
                                </Drawer>
                            </PageWrapper>
                        </FlexContainer>
                    </GridColumn>
                </Grid>
                {displayWidgetPermissionModal && (
                    <WidgetPermissionModal
                        onClose={() => setDisplayWidgetPermissionModal(null)}
                        widgetId={displayWidgetPermissionModal.widgetId}
                        managing={displayWidgetPermissionModal.managing}
                    />
                )}
            </Page>
        </PermissionsContext.Provider>
    );
}

export default Home;
